/**
* Locale content pool for /about, /contact, /coverage across 6 locales
* (en, zh, ja, ko, vi, id). Feeds LocaleAboutRich / LocaleContactRich /
* LocaleCoverageRich to keep each page long-form (A5 quality) with
* Answer Summary (AEO), FAQ (FAQPage schema), and localized JSON-LD.
*
* Content is native-quality translation — not machine — targeting the same
* intent keywords as the Thai originals but written for each locale's
* audience (embassies, expats, HR, students, immigrants).
*/

export type LocaleCode = "en" | "zh" | "ja" | "ko" | "vi" | "id";

export interface LocaleAboutContent {
  metaTitle: string;
  metaDesc: string;
  eyebrow: string;
  heroTitle: string;
  heroSubtitle: string;
  answerLabel: string;
  answer: string;
  aboutHeading: string;
  intro: string;
  credentialsHeading: string;
  credentials: string[];
  teamHeading: string;
  teamCaption: string;
  languagesLabel: string;
  faqHeading: string;
  faq: { q: string; a: string }[];
  breadcrumbHome: string;
  breadcrumbSelf: string;
}

export interface LocaleContactContent {
  metaTitle: string;
  metaDesc: string;
  eyebrow: string;
  heroTitle: string;
  heroSubtitle: string;
  answerLabel: string;
  answer: string;
  contactHeading: string;
  intro: string;
  headOfficeLabel: string;
  hoursLabel: string;
  lineLabel: string;
  phoneLabel: string;
  emailLabel: string;
  quickHelpHeading: string;
  quickHelp: { title: string; body: string }[];
  bookingHeading: string;
  bookingBullets: string[];
  faqHeading: string;
  faq: { q: string; a: string }[];
  ctaCall: string;
  ctaLine: string;
  breadcrumbHome: string;
  breadcrumbSelf: string;
}

export interface LocaleCoverageContent {
  metaTitle: string;
  metaDesc: string;
  eyebrow: string;
  heroTitle: string;
  heroSubtitle: string;
  answerLabel: string;
  answer: string;
  overviewHeading: string;
  intro: string;
  officesHeading: string;
  officeBullets: string[];
  bangkokHeading: string;
  provincesHeading: string;
  regionLabels: Record<"bangkok" | "central" | "north" | "northeast" | "east" | "west" | "south", string>;
  bangkokDistrictsLabel: string;
  perAreaCaption: string;
  faqHeading: string;
  faq: { q: string; a: string }[];
  breadcrumbHome: string;
  breadcrumbSelf: string;
}

/* ------------------------------------------------------------------ */
/* ABOUT */
/* ------------------------------------------------------------------ */

export const LOCALE_ABOUT: Record<LocaleCode, LocaleAboutContent> = {
  en: {
    metaTitle: "About Thai Notary Law — Licensed Attorneys, Bangkok",
    metaDesc:
      "Six licensed Notarial Services Attorneys registered with the Lawyers Council of Thailand. Bangkok HQ plus Khon Kaen, Udon Thani, Nong Khai offices.",
    eyebrow: "English · About",
    heroTitle: "Six licensed Notarial Services Attorneys",
    heroSubtitle:
      "Established 2022 · registered with the Lawyers Council of Thailand · offices in Bangkok, Khon Kaen, Udon Thani and Nong Khai.",
    answerLabel: "Answer summary",
    answer:
      "Thai Notary Law & Service Co., Ltd. is a Bangkok-headquartered law firm led by six Notarial Services Attorneys licensed under the Lawyers Council of Thailand Regulation B.E. 2551 (2008). Every attorney holds a valid Notarial Services License and specializes in notarization, MOFA legalization, embassy authentication, sworn translation and cross-border document work for individuals, corporations, embassies and universities.",
    aboutHeading: "Who we are",
    intro:
      "Thai Notary Law was founded in 2022 by senior attorneys with more than a decade of combined experience in notarial services, immigration paperwork and international legalization. Our practice is built on one principle: every certified copy, every notarized signature and every legalized document that leaves our office must be accepted by the receiving authority on the first attempt — whether that authority is a US university admissions office, the Chinese embassy, a Japanese immigration bureau or the German Ministry of the Interior. We handle over 400 files per month and maintain a documented 99% first-time acceptance rate across MOFA and embassy channels.",
    credentialsHeading: "Credentials & compliance",
    credentials: [
      "Licensed Notarial Services Attorneys under the Lawyers Council of Thailand (สภาทนายความในพระบรมราชูปถัมภ์) Regulation B.E. 2551 (2008).",
      "Company registered with the Department of Business Development (DBD), Ministry of Commerce.",
      "Sworn translators recognized by Thai courts and by multiple foreign embassies in Bangkok.",
      "Full PDPA compliance — client data is encrypted, access-logged and retained only for the statutory period.",
      "Fixed-fee quotes issued in writing before any work begins — no hidden charges.",
      "Documented chain of custody for every original document held on our premises.",
    ],
    teamHeading: "Our attorneys",
    teamCaption: "Every attorney below holds an active Notarial Services License. Click a name for their license image and specialties.",
    languagesLabel: "Languages",
    faqHeading: "Questions clients ask before hiring us",
    faq: [
      {
        q: "How do I verify that your attorneys are really licensed?",
        a: "Each attorney's Notarial Services License is displayed on our /about page and on our office wall. You can cross-check the license number and name at the Lawyers Council of Thailand (Sapha Thanai Khwam) office in Bangkok or via their online registry.",
      },
      {
        q: "Do you handle documents in languages other than English and Thai?",
        a: "Yes. Our in-house translators work in English, Chinese (simplified & traditional), Japanese, Korean, Vietnamese and Indonesian. For other languages we partner with sworn translators recognized by the relevant embassies.",
      },
      {
        q: "Can a lawyer travel to my office, hotel or hospital for notarization?",
        a: "Yes — we offer mobile notarization anywhere in Bangkok and neighboring provinces (Nonthaburi, Pathum Thani, Samut Prakan, Samut Sakhon, Nakhon Pathom). Travel fees apply and are quoted before dispatch.",
      },
      {
        q: "How long have you been in business?",
        a: "Thai Notary Law & Service Co., Ltd. was incorporated in 2022, but our founding attorneys have been performing notarial services since 2013 under previous practice names. Our combined firm-level experience exceeds forty attorney-years.",
      },
      {
        q: "Are you insured?",
        a: "Yes. The firm carries professional indemnity insurance covering notarial and legalization services. Certificates are available on request for corporate clients.",
      },
      {
        q: "Do you accept walk-ins?",
        a: "Walk-ins are welcome at our Wang Thonglang headquarters, Monday-Sunday 09:00-18:00. Same-day service is available if you arrive before 14:00 with all required originals.",
      },
    ],
    breadcrumbHome: "Home",
    breadcrumbSelf: "About",
  },

  zh: {
    metaTitle: "关于Thai Notary Law — 曼谷六位持证公证律师 | 泰国律师协会注册",
    metaDesc:
      "六位在泰国律师协会注册的持证公证律师，曼谷总部及孔敬、乌隆他尼、廊开分所。自2022年起为使领馆、大学、跨国企业提供公证、领事认证、宣誓翻译服务。",
    eyebrow: "中文 · 关于我们",
    heroTitle: "六位泰国持证公证律师",
    heroSubtitle:
      "2022年成立 · 在泰国律师协会正式注册 · 曼谷、孔敬、乌隆他尼、廊开设有办公室。",
    answerLabel: "快速回答",
    answer:
      "Thai Notary Law & Service Co., Ltd.（泰国公证法律服务有限公司）是位于曼谷的律师事务所，由六位依据佛历2551年(2008年)《签名及文件公证律师注册条例》取得泰国律师协会公证律师执照的资深律师组成，专办公证、泰国外交部认证、使领馆认证、宣誓翻译及跨境文件办理，服务对象包括个人、企业、使领馆及各国大学。",
    aboutHeading: "关于我们",
    intro:
      "Thai Notary Law于2022年由数位从业逾十年的资深律师共同创立，专精公证、移民文件与国际认证业务。我们奉行一个原则：从我们办公室出去的每一份认证副本、每一份公证签名、每一份领事认证文件，都必须一次通过收件机关审核——无论是美国大学招生办、中国大使馆、日本入国管理局，还是德国内政部。我们每月处理超过400份案件，泰国外交部与使领馆一次通过率达99%以上，全程有据可查。",
    credentialsHeading: "资质与合规",
    credentials: [
      "依据佛历2551年(2008年)《签名及文件公证律师注册条例》，六位律师均持有泰国律师协会（สภาทนายความในพระบรมราชูปถัมภ์）颁发的公证律师执照。",
      "公司在泰国商务部商业发展厅(DBD)正式注册。",
      "宣誓翻译获泰国法院及曼谷多国使领馆认可。",
      "严格遵守泰国《个人数据保护法》(PDPA) — 客户数据加密存储、访问留痕、法定期限后销毁。",
      "开工前以书面形式提供固定报价 — 绝无隐藏费用。",
      "所有原件在本所保管期间均有完整的交接记录链。",
    ],
    teamHeading: "我们的律师",
    teamCaption: "以下每位律师均持有效期内的公证律师执照，点击姓名可查看执照原件及专业领域。",
    languagesLabel: "语言",
    faqHeading: "客户委托前常问的问题",
    faq: [
      {
        q: "如何核实贵所律师的执照真伪？",
        a: "每位律师的公证律师执照都陈列于本网站/about页面及本所办公室墙面。您可以持执照编号及姓名到曼谷的泰国律师协会办公室或其在线注册系统直接核对。",
      },
      {
        q: "除中英泰文外，贵所是否办理其他语种文件？",
        a: "是。本所内部翻译团队涵盖英、中（简繁）、日、韩、越南、印尼语。其他语种由本所与相关使领馆认可的宣誓翻译合作办理。",
      },
      {
        q: "律师可以到我的办公室、酒店或医院上门公证吗？",
        a: "可以。本所提供曼谷及周边府（暖武里、巴吞他尼、北榄、龙仔厝、佛统）上门公证服务，出发前会先书面报价包含差旅费。",
      },
      {
        q: "贵所成立多久了？",
        a: "Thai Notary Law于2022年正式设立公司，但创所律师从2013年起即以其他事务所名义执业公证业务，本所律师累计执业年资超过四十年。",
      },
      {
        q: "是否投保？",
        a: "本所已投保律师执业责任险，涵盖公证与认证业务，企业客户可来函索取保单证明。",
      },
      {
        q: "可以直接到访不预约吗？",
        a: "欢迎直接到访本所曼谷Wang Thonglang总部，营业时间周一至周日09:00-18:00。若于14:00前到达并携带全部原件，可申请当日出件。",
      },
    ],
    breadcrumbHome: "首页",
    breadcrumbSelf: "关于我们",
  },

  ja: {
    metaTitle: "Thai Notary Law について — バンコクの公証弁護士6名 | タイ弁護士会正式登録",
    metaDesc:
      "タイ弁護士会正式登録の公証弁護士6名。バンコク本部及びコーンケン・ウドンタニ・ノーンカーイに支所。2022年以来、大使館・大学・多国籍企業へ公証・領事認証・宣誓翻訳を提供。",
    eyebrow: "日本語 · 私たちについて",
    heroTitle: "タイ弁護士会正式登録の公証弁護士6名",
    heroSubtitle:
      "2022年設立 · タイ弁護士会正式登録 · バンコク・コーンケン・ウドンタニ・ノーンカーイに事務所。",
    answerLabel: "要約回答",
    answer:
      "Thai Notary Law & Service Co., Ltd.（タイ公証法律サービス株式会社）は、仏歴2551年（2008年）「署名及び文書公証弁護士登録規則」に基づきタイ弁護士会公証弁護士免許を保有する6名の弁護士が率いる、バンコクの法律事務所です。個人・法人・大使館・大学向けに、公証、タイ外務省認証、大使館認証、宣誓翻訳、国際文書手続を専門としています。",
    aboutHeading: "私たちについて",
    intro:
      "Thai Notary Lawは2022年、公証・移民書類・国際認証の実務経験10年以上の弁護士らにより設立されました。私たちが守る原則はひとつ — 事務所から発送されるすべての認証謄本、すべての公証署名、すべての領事認証文書は、米国大学入学課、中国大使館、日本の入管、ドイツ内務省など、受取機関で一発通過しなければならない、というものです。月間400件超を処理し、タイ外務省及び大使館ルートでの一発通過率は文書記録上99%を維持しています。",
    credentialsHeading: "資格と法令遵守",
    credentials: [
      "6名全員が仏歴2551年（2008年）「署名及び文書公証弁護士登録規則」に基づきタイ弁護士会（สภาทนายความในพระบรมราชูปถัมภ์）発行の公証弁護士免許を保有。",
      "タイ商務省事業開発局(DBD)にて正式登記済み。",
      "タイ裁判所及びバンコク駐在の複数国大使館が認定する宣誓翻訳者。",
      "タイ個人情報保護法(PDPA)完全遵守 — お客様データは暗号化・アクセス履歴管理・法定期間経過後に廃棄。",
      "着手前に書面で固定料金を提示 — 追加費用の隠し請求は一切なし。",
      "本所預り原本は全て入出庫記録を保持。",
    ],
    teamHeading: "所属弁護士",
    teamCaption: "下記各弁護士は有効な公証弁護士免許を保有。氏名をクリックすると免許画像と得意分野をご覧いただけます。",
    languagesLabel: "対応言語",
    faqHeading: "ご依頼前によくいただくご質問",
    faq: [
      {
        q: "貴所弁護士の免許は本物ですか、どう確認できますか？",
        a: "各弁護士の公証弁護士免許は本サイト/aboutページ及び当所事務所壁面に掲示しています。免許番号と氏名をお持ちいただければ、バンコクのタイ弁護士会事務局又はオンライン登録システムにて直接照合可能です。",
      },
      {
        q: "英語・タイ語以外の言語文書も扱えますか？",
        a: "はい。所内翻訳陣は英語・中国語（簡繁）・日本語・韓国語・ベトナム語・インドネシア語に対応。その他言語は各国大使館認定の宣誓翻訳者と提携して対応します。",
      },
      {
        q: "オフィス・ホテル・病院への出張公証は可能ですか？",
        a: "可能です。バンコク及び近郊県（ノンタブリー、パトゥムターニー、サムットプラーカーン、サムットサーコーン、ナコーンパトム）へ出張公証を承ります。出張費は事前書面見積に含めます。",
      },
      {
        q: "創業何年ですか？",
        a: "Thai Notary Lawは2022年に法人化しましたが、創業弁護士は2013年より別名義で公証業務に従事しており、事務所全体の累計弁護士経験年数は40年を超えます。",
      },
      {
        q: "保険には加入していますか？",
        a: "はい。弁護士業務賠償責任保険（公証・認証業務を含む）に加入しています。法人のお客様には保険証明を書面でご提供可能です。",
      },
      {
        q: "予約なしで訪問できますか？",
        a: "バンコク・ワントンラン本店へは予約なしでもご来所いただけます。営業時間は月〜日 09:00-18:00。14:00までに必要原本をお持ちの上ご来所の場合、当日発行可能です。",
      },
    ],
    breadcrumbHome: "ホーム",
    breadcrumbSelf: "私たちについて",
  },

  ko: {
    metaTitle: "Thai Notary Law 소개 — 방콕 공증변호사 6인 | 태국변호사협회 정식 등록",
    metaDesc:
      "태국변호사협회 정식 등록 공증변호사 6인. 방콕 본사와 콘깬·우돈타니·농카이 지사. 2022년부터 대사관·대학·다국적기업에 공증·영사인증·선서번역 서비스 제공.",
    eyebrow: "한국어 · 소개",
    heroTitle: "태국변호사협회 등록 공증변호사 6인",
    heroSubtitle:
      "2022년 설립 · 태국변호사협회 정식 등록 · 방콕·콘깬·우돈타니·농카이 사무소.",
    answerLabel: "요약 답변",
    answer:
      "Thai Notary Law & Service Co., Ltd.는 불력 2551년(2008년) '서명 및 문서 공증변호사 등록 규정'에 따라 태국변호사협회 공증변호사 자격을 보유한 6명의 변호사가 이끄는 방콕 소재 법률사무소입니다. 개인·기업·대사관·대학을 대상으로 공증, 태국 외무부 인증, 대사관 영사인증, 선서번역, 국제 문서 처리를 전문으로 합니다.",
    aboutHeading: "소개",
    intro:
      "Thai Notary Law는 2022년, 공증·이민서류·국제인증 분야에서 10년 이상의 경력을 가진 변호사들에 의해 설립되었습니다. 우리는 하나의 원칙을 지킵니다 — 사무소에서 나가는 모든 인증사본, 모든 공증서명, 모든 영사인증 문서는 미국 대학 입학처, 중국 대사관, 일본 입국관리국, 독일 내무부 등 접수기관에서 한 번에 통과되어야 한다는 것입니다. 월 400건 이상을 처리하며, 태국 외무부 및 대사관 채널에서 문서화된 최초 통과율 99%를 유지하고 있습니다.",
    credentialsHeading: "자격과 컴플라이언스",
    credentials: [
      "6명 전원이 불력 2551년(2008년) 규정에 따라 태국변호사협회(สภาทนายความในพระบรมราชูปถัมภ์)가 발급한 공증변호사 자격증 보유.",
      "태국 상무부 사업개발국(DBD)에 정식 등록된 법인.",
      "태국 법원 및 방콕 주재 여러 대사관이 인정하는 선서번역사.",
      "태국 개인정보보호법(PDPA) 완전 준수 — 고객 데이터 암호화, 접근 기록, 법정 기간 후 폐기.",
      "착수 전 서면 확정 견적 제공 — 숨겨진 비용 일절 없음.",
      "본 사무소 보관 원본은 모든 입출고 기록 유지.",
    ],
    teamHeading: "소속 변호사",
    teamCaption: "아래 각 변호사는 유효한 공증변호사 자격증을 보유하고 있습니다. 이름을 클릭하면 자격증 이미지와 전문 분야를 확인할 수 있습니다.",
    languagesLabel: "사용 언어",
    faqHeading: "의뢰 전 자주 묻는 질문",
    faq: [
      {
        q: "귀 사무소 변호사 자격증의 진위를 어떻게 확인할 수 있나요?",
        a: "각 변호사의 공증변호사 자격증은 본 웹사이트 /about 페이지 및 사무소 벽면에 게시되어 있습니다. 자격증 번호와 성명으로 방콕 태국변호사협회 사무국이나 온라인 등록시스템에서 직접 확인하실 수 있습니다.",
      },
      {
        q: "영어와 태국어 외 다른 언어 문서도 처리 가능한가요?",
        a: "네. 사내 번역팀이 영어·중국어(간체/번체)·일본어·한국어·베트남어·인도네시아어를 지원합니다. 기타 언어는 해당 대사관이 인정하는 선서번역사와 제휴하여 처리합니다.",
      },
      {
        q: "사무실·호텔·병원으로 방문 공증이 가능한가요?",
        a: "가능합니다. 방콕 및 인근 도(논타부리, 파툼타니, 사뭇쁘라깐, 사뭇사콘, 나콘빠톰)로 방문 공증 서비스를 제공하며, 출장비는 사전 서면 견적에 포함됩니다.",
      },
      {
        q: "설립된 지 얼마나 되었나요?",
        a: "Thai Notary Law는 2022년 법인 설립되었으나, 창립 변호사들은 2013년부터 다른 사무소명으로 공증 업무를 수행해왔으며, 사무소 전체 누적 변호사 경력은 40년을 초과합니다.",
      },
      {
        q: "보험에 가입되어 있나요?",
        a: "네. 변호사 업무 배상책임보험(공증·인증 업무 포함)에 가입되어 있으며, 법인 고객에게는 보험증명서를 서면으로 제공 가능합니다.",
      },
      {
        q: "예약 없이 방문 가능한가요?",
        a: "방콕 왕통랑 본사는 예약 없이도 방문 가능합니다. 영업시간은 월~일 09:00-18:00이며, 14:00 이전에 필요한 원본을 지참하고 방문하시면 당일 발급이 가능합니다.",
      },
    ],
    breadcrumbHome: "홈",
    breadcrumbSelf: "소개",
  },

  vi: {
    metaTitle: "Về Thai Notary Law — 6 Luật sư Công chứng được cấp phép tại Bangkok",
    metaDesc:
      "Sáu luật sư công chứng đăng ký chính thức với Hội Luật sư Thái Lan. Trụ sở Bangkok cùng văn phòng Khon Kaen, Udon Thani, Nong Khai. Phục vụ đại sứ quán, trường đại học và tập đoàn đa quốc gia từ 2022.",
    eyebrow: "Tiếng Việt · Giới thiệu",
    heroTitle: "Sáu luật sư công chứng được cấp phép",
    heroSubtitle:
      "Thành lập 2022 · Đăng ký chính thức với Hội Luật sư Thái Lan · Văn phòng Bangkok, Khon Kaen, Udon Thani, Nong Khai.",
    answerLabel: "Tóm tắt trả lời",
    answer:
      "Thai Notary Law & Service Co., Ltd. là công ty luật đặt trụ sở tại Bangkok do sáu luật sư công chứng được cấp phép theo Quy định B.E. 2551 (2008) của Hội Luật sư Thái Lan điều hành. Chuyên công chứng, hợp pháp hóa Bộ Ngoại giao Thái, chứng thực đại sứ quán, dịch tuyên thệ và xử lý hồ sơ xuyên biên giới cho cá nhân, doanh nghiệp, đại sứ quán và trường đại học.",
    aboutHeading: "Về chúng tôi",
    intro:
      "Thai Notary Law được thành lập năm 2022 bởi các luật sư cấp cao với hơn một thập kỷ kinh nghiệm về công chứng, hồ sơ nhập cư và hợp pháp hóa quốc tế. Chúng tôi tuân thủ một nguyên tắc: mọi bản sao chứng thực, mọi chữ ký công chứng, mọi hồ sơ hợp pháp hóa rời khỏi văn phòng chúng tôi phải được cơ quan tiếp nhận chấp nhận ngay lần đầu — dù đó là phòng tuyển sinh đại học Mỹ, đại sứ quán Trung Quốc, cục nhập cư Nhật Bản hay Bộ Nội vụ Đức. Chúng tôi xử lý hơn 400 hồ sơ mỗi tháng và duy trì tỷ lệ chấp nhận lần đầu 99% được ghi chép qua kênh Bộ Ngoại giao Thái và đại sứ quán.",
    credentialsHeading: "Chứng chỉ & Tuân thủ",
    credentials: [
      "Sáu luật sư đều có giấy phép Luật sư Công chứng do Hội Luật sư Thái Lan (สภาทนายความในพระบรมราชูปถัมภ์) cấp theo Quy định B.E. 2551 (2008).",
      "Công ty đăng ký tại Cục Phát triển Doanh nghiệp (DBD), Bộ Thương mại Thái Lan.",
      "Dịch giả tuyên thệ được Tòa án Thái Lan và nhiều đại sứ quán nước ngoài tại Bangkok công nhận.",
      "Tuân thủ đầy đủ Luật Bảo vệ Dữ liệu Cá nhân Thái Lan (PDPA) — dữ liệu khách hàng được mã hóa, ghi log truy cập và tiêu hủy sau thời hạn luật định.",
      "Báo giá cố định bằng văn bản trước khi bắt đầu công việc — không có phí ẩn.",
      "Chuỗi bàn giao có tài liệu cho mọi bản gốc lưu tại văn phòng.",
    ],
    teamHeading: "Đội ngũ luật sư",
    teamCaption: "Mỗi luật sư dưới đây đều có giấy phép Luật sư Công chứng còn hiệu lực. Nhấp vào tên để xem hình ảnh giấy phép và chuyên môn.",
    languagesLabel: "Ngôn ngữ",
    faqHeading: "Câu hỏi khách hàng thường hỏi trước khi thuê",
    faq: [
      {
        q: "Làm sao xác minh luật sư của quý công ty thực sự có giấy phép?",
        a: "Giấy phép Luật sư Công chứng của mỗi luật sư được trưng bày trên trang /about và trên tường văn phòng. Bạn có thể kiểm tra chéo số giấy phép và tên tại văn phòng Hội Luật sư Thái Lan ở Bangkok hoặc qua hệ thống đăng ký trực tuyến.",
      },
      {
        q: "Quý công ty có xử lý tài liệu ngôn ngữ khác ngoài Anh và Thái không?",
        a: "Có. Đội dịch nội bộ làm việc bằng tiếng Anh, Trung (giản thể & phồn thể), Nhật, Hàn, Việt và Indonesia. Ngôn ngữ khác, chúng tôi hợp tác với dịch giả tuyên thệ được các đại sứ quán liên quan công nhận.",
      },
      {
        q: "Luật sư có thể đến văn phòng, khách sạn hoặc bệnh viện của tôi để công chứng không?",
        a: "Có — chúng tôi cung cấp dịch vụ công chứng lưu động tại Bangkok và các tỉnh lân cận (Nonthaburi, Pathum Thani, Samut Prakan, Samut Sakhon, Nakhon Pathom). Phí di chuyển được báo giá trước khi khởi hành.",
      },
      {
        q: "Quý công ty hoạt động bao lâu rồi?",
        a: "Thai Notary Law & Service Co., Ltd. được thành lập năm 2022, nhưng các luật sư sáng lập đã thực hiện công chứng từ 2013 dưới các tên hành nghề trước đó. Tổng kinh nghiệm cấp công ty vượt bốn mươi năm luật sư.",
      },
      {
        q: "Có bảo hiểm không?",
        a: "Có. Công ty có bảo hiểm trách nhiệm nghề nghiệp bao gồm dịch vụ công chứng và hợp pháp hóa. Chứng nhận có sẵn theo yêu cầu cho khách hàng doanh nghiệp.",
      },
      {
        q: "Có tiếp khách vãng lai không?",
        a: "Khách vãng lai được chào đón tại trụ sở Wang Thonglang, Thứ Hai-Chủ Nhật 09:00-18:00. Dịch vụ trong ngày có sẵn nếu bạn đến trước 14:00 với đầy đủ bản gốc cần thiết.",
      },
    ],
    breadcrumbHome: "Trang chủ",
    breadcrumbSelf: "Giới thiệu",
  },

  id: {
    metaTitle: "Tentang Thai Notary Law — Enam Notaris Berlisensi di Bangkok",
    metaDesc:
      "Enam Notaris Publik berlisensi terdaftar di Dewan Pengacara Thailand. Kantor pusat Bangkok serta cabang di Khon Kaen, Udon Thani dan Nong Khai. Melayani kedutaan, universitas dan perusahaan multinasional sejak 2022.",
    eyebrow: "Bahasa Indonesia · Tentang",
    heroTitle: "Enam Notaris Publik berlisensi",
    heroSubtitle:
      "Didirikan 2022 · terdaftar resmi di Dewan Pengacara Thailand · kantor di Bangkok, Khon Kaen, Udon Thani dan Nong Khai.",
    answerLabel: "Ringkasan jawaban",
    answer:
      "Thai Notary Law & Service Co., Ltd. adalah firma hukum berkantor pusat di Bangkok yang dipimpin oleh enam Notaris Publik berlisensi berdasarkan Peraturan Dewan Pengacara Thailand B.E. 2551 (2008). Melayani individu, korporasi, kedutaan besar, dan universitas untuk notarisasi, legalisasi Kementerian Luar Negeri Thailand, autentikasi kedutaan, terjemahan tersumpah dan penanganan dokumen lintas negara.",
    aboutHeading: "Tentang kami",
    intro:
      "Thai Notary Law didirikan pada 2022 oleh para pengacara senior dengan pengalaman gabungan lebih dari sepuluh tahun di bidang notarisasi, dokumen imigrasi dan legalisasi internasional. Kami berpegang pada satu prinsip: setiap salinan tersertifikasi, setiap tanda tangan yang dinotarisasi, dan setiap dokumen yang dilegalisasi yang keluar dari kantor kami harus diterima oleh instansi penerima pada percobaan pertama — baik itu bagian penerimaan universitas Amerika, Kedutaan Besar Cina, imigrasi Jepang, atau Kementerian Dalam Negeri Jerman. Kami menangani lebih dari 400 berkas per bulan dan mempertahankan tingkat penerimaan pertama 99% yang terdokumentasi melalui kanal Kementerian Luar Negeri Thailand dan kedutaan.",
    credentialsHeading: "Kredensial & kepatuhan",
    credentials: [
      "Enam pengacara memiliki Lisensi Notaris Publik yang dikeluarkan Dewan Pengacara Thailand (สภาทนายความในพระบรมราชูปถัมภ์) berdasarkan Peraturan B.E. 2551 (2008).",
      "Perusahaan terdaftar di Departemen Pengembangan Bisnis (DBD), Kementerian Perdagangan Thailand.",
      "Penerjemah tersumpah diakui pengadilan Thailand dan sejumlah kedutaan asing di Bangkok.",
      "Kepatuhan penuh terhadap Undang-Undang Perlindungan Data Pribadi Thailand (PDPA) — data klien dienkripsi, akses dicatat, dan dimusnahkan setelah masa retensi wajib.",
      "Penawaran harga tetap tertulis sebelum pekerjaan dimulai — tanpa biaya tersembunyi.",
      "Rantai pengawasan terdokumentasi untuk setiap dokumen asli yang dititipkan.",
    ],
    teamHeading: "Tim pengacara kami",
    teamCaption: "Setiap pengacara di bawah ini memegang Lisensi Notaris Publik yang aktif. Klik nama untuk melihat gambar lisensi dan spesialisasi.",
    languagesLabel: "Bahasa",
    faqHeading: "Pertanyaan yang klien tanyakan sebelum menyewa",
    faq: [
      {
        q: "Bagaimana saya memverifikasi bahwa pengacara Anda benar-benar berlisensi?",
        a: "Lisensi Notaris Publik setiap pengacara ditampilkan di halaman /about kami dan di dinding kantor. Anda dapat memverifikasi nomor lisensi dan nama di kantor Dewan Pengacara Thailand di Bangkok atau melalui sistem registri online mereka.",
      },
      {
        q: "Apakah Anda menangani dokumen dalam bahasa selain Inggris dan Thai?",
        a: "Ya. Tim penerjemah internal kami bekerja dalam bahasa Inggris, Mandarin (aksara sederhana & tradisional), Jepang, Korea, Vietnam, dan Indonesia. Untuk bahasa lain, kami bermitra dengan penerjemah tersumpah yang diakui kedutaan terkait.",
      },
      {
        q: "Bisakah pengacara datang ke kantor, hotel, atau rumah sakit saya untuk notarisasi?",
        a: "Ya — kami menawarkan notarisasi keliling di mana saja di Bangkok dan provinsi tetangga (Nonthaburi, Pathum Thani, Samut Prakan, Samut Sakhon, Nakhon Pathom). Biaya perjalanan berlaku dan dikutip sebelum keberangkatan.",
      },
      {
        q: "Sudah berapa lama Anda beroperasi?",
        a: "Thai Notary Law & Service Co., Ltd. didirikan pada 2022, tetapi para pengacara pendiri telah melakukan layanan notaris sejak 2013 di bawah nama praktik sebelumnya. Pengalaman gabungan tingkat firma kami melebihi empat puluh tahun-pengacara.",
      },
      {
        q: "Apakah Anda diasuransikan?",
        a: "Ya. Firma memiliki asuransi tanggung jawab profesional yang mencakup layanan notaris dan legalisasi. Sertifikat tersedia atas permintaan klien korporat.",
      },
      {
        q: "Apakah Anda menerima klien tanpa janji temu?",
        a: "Klien tanpa janji temu diterima di kantor pusat Wang Thonglang, Senin-Minggu 09:00-18:00. Layanan hari yang sama tersedia jika Anda tiba sebelum 14:00 dengan semua dokumen asli yang diperlukan.",
      },
    ],
    breadcrumbHome: "Beranda",
    breadcrumbSelf: "Tentang",
  },
};

/* ------------------------------------------------------------------ */
/* CONTACT */
/* ------------------------------------------------------------------ */

export const LOCALE_CONTACT: Record<LocaleCode, LocaleContactContent> = {
  en: {
    metaTitle: "Contact Thai Notary Law — Bangkok Notary Public & Legalization",
    metaDesc:
      "Contact a licensed Bangkok notary today. LINE, phone, email and walk-in address in Wang Thonglang (Bangkok 10310). Open Mon-Sun 09:00-18:00. Free quotes.",
    eyebrow: "English · Contact",
    heroTitle: "Reach a licensed Bangkok notary today",
    heroSubtitle: "Free consultation on LINE · same-day quotes · walk-ins welcome Mon-Sun 09:00-18:00.",
    answerLabel: "Answer summary",
    answer:
      "Thai Notary Law's head office is at 62/1 Wang Thonglang, Bangkok 10310 (LINE @Thainotary, phone +66 94 895 8999, email Contact@thainotary.co.th), open Monday to Sunday 09:00-18:00. Free written quotes are issued within one business hour. Mobile notarization is available across Bangkok and neighboring provinces on same-day dispatch.",
    contactHeading: "How to reach us",
    intro:
      "For fastest response, send a photo of the documents you need certified over LINE and we will reply with a fixed written quote within one business hour. Walk-ins are welcome at our Wang Thonglang headquarters every day of the week. For corporate clients we accept purchase orders, issue tax invoices, and can dispatch a notary to your office anywhere in Bangkok, Nonthaburi, Pathum Thani, Samut Prakan, Samut Sakhon or Nakhon Pathom on the same day.",
    headOfficeLabel: "Head office",
    hoursLabel: "Opening hours",
    lineLabel: "LINE",
    phoneLabel: "Phone",
    emailLabel: "Email",
    quickHelpHeading: "Quick help by scenario",
    quickHelp: [
      { title: "Studying abroad", body: "Bring your passport, diploma and transcript. We handle notary + MOFA + embassy legalization end-to-end." },
      { title: "Marriage or divorce abroad", body: "We prepare and notarize affidavits, certified translations, and legalize them for embassies (US, UK, Germany, Japan, Australia, EU)." },
      { title: "Company/board resolutions", body: "Same-day corporate document notarization; we accept e-signed originals for POAs and shareholder resolutions." },
      { title: "Property abroad", body: "Notarized POAs, statutory declarations and passport certifications accepted by land offices worldwide." },
    ],
    bookingHeading: "Booking tips",
    bookingBullets: [
      "Send document photos on LINE for the fastest fixed quote (usually within one business hour).",
      "Bring original documents and a valid ID (passport for foreigners) on the day of notarization.",
      "Arrive before 14:00 for same-day service; after 14:00 we book for the next business day.",
      "For MOFA + embassy chains, allow 3–7 business days depending on the receiving country.",
      "Corporate accounts (net-15 invoicing) available on request.",
    ],
    faqHeading: "Contact FAQ",
    faq: [
      { q: "How quickly do you reply on LINE?", a: "Within one business hour during opening hours (09:00-18:00 daily). Outside hours we reply first thing the next morning." },
      { q: "Do you charge for the initial consultation?", a: "No. All quotes and initial consultations are free — including document review and workflow planning." },
      { q: "Can you send a notary to my hotel or hospital?", a: "Yes, mobile notarization is available across Bangkok metropolitan area. Travel fees are quoted before dispatch." },
      { q: "Do you accept credit cards?", a: "Yes — Visa, Mastercard, JCB, and PromptPay QR. Corporate wire transfers are also accepted with net-15 terms on request." },
      { q: "Can I pay in USD, EUR or CNY?", a: "Yes, international bank transfers in USD, EUR, GBP, JPY, CNY, KRW, VND, IDR are accepted for legal-service invoices." },
      { q: "Do you have parking?", a: "Yes, free customer parking is available at the Wang Thonglang office. Wang Thonglang MRT is a 6-minute walk." },
    ],
    ctaCall: "Call now",
    ctaLine: "Chat on LINE",
    breadcrumbHome: "Home",
    breadcrumbSelf: "Contact",
  },

  zh: {
    metaTitle: "联系Thai Notary Law — 曼谷公证律师与领事认证",
    metaDesc:
      "立即联系曼谷持证公证律师。可通过 LINE、电话、邮箱或直接到访 Wang Thonglang 办公室（曼谷10310）。周一至周日 09:00-18:00 营业，中文接待，先看文件再报价，全程免费咨询。",
    eyebrow: "中文 · 联系我们",
    heroTitle: "立即联系曼谷持证公证律师",
    heroSubtitle: "LINE免费咨询 · 当日报价 · 欢迎到访 周一至周日 09:00-18:00。",
    answerLabel: "快速回答",
    answer:
      "Thai Notary Law总部位于曼谷Wang Thonglang区62/1，邮编10310，LINE ID @Thainotary、电话 +66 94 895 8999、邮箱 Contact@thainotary.co.th，营业时间周一至周日09:00-18:00。书面固定报价一小时内出具，曼谷及周边府可当日派遣律师上门公证。",
    contactHeading: "联系方式",
    intro:
      "为获得最快回复，请将需要认证的文件拍照发送至LINE，我们将于一个工作小时内以书面形式给出固定报价。本所欢迎全周任何一天直接到访Wang Thonglang总部。企业客户可采购单结算并开具税务发票，可派遣律师上门至曼谷、暖武里、巴吞他尼、北榄、龙仔厝、佛统等地办公。",
    headOfficeLabel: "总部地址",
    hoursLabel: "营业时间",
    lineLabel: "LINE",
    phoneLabel: "电话",
    emailLabel: "邮箱",
    quickHelpHeading: "常见场景快速指引",
    quickHelp: [
      { title: "出国留学", body: "携带护照、学位证、成绩单，本所全程办理公证+泰外交部+使馆认证。" },
      { title: "海外结婚离婚", body: "起草公证声明书、宣誓翻译，并办理美国、英国、德国、日本、澳大利亚、欧盟等使馆认证。" },
      { title: "公司/董事会决议", body: "当日办理公司文件公证；接受授权书及股东决议的电子签名原件。" },
      { title: "海外房产", body: "公证授权书、法定声明、护照认证，全球土地局均予接受。" },
    ],
    bookingHeading: "预约小贴士",
    bookingBullets: [
      "文件拍照发LINE最快获得固定报价（通常一小时内）。",
      "公证当日请携带原件及有效身份证件（外籍请带护照）。",
      "14:00前到访可当日办结；14:00后预约次工作日办理。",
      "外交部+使馆链条视目的国家不同，通常需3–7个工作日。",
      "如需企业月结账户（Net-15）请另行提出。",
    ],
    faqHeading: "联系相关常见问题",
    faq: [
      { q: "LINE咨询多久有回复？", a: "营业时间内（每日09:00-18:00）一个工作小时内回复。非营业时段将于次日一早回复。" },
      { q: "初次咨询收费吗？", a: "不收。所有报价及初次咨询均免费，包括文件审阅与流程规划。" },
      { q: "律师可以去我酒店或医院公证吗？", a: "可以，曼谷大都市区均可提供上门公证服务，差旅费于派遣前书面报价。" },
      { q: "接受哪些付款方式？", a: "Visa、Mastercard、JCB、PromptPay二维码，可申请Net-15企业电汇。" },
      { q: "可以用美金、欧元或人民币付款吗？", a: "可以，法律服务发票接受美金、欧元、英镑、日元、人民币、韩元、越南盾、印尼盾国际电汇。" },
      { q: "有停车位吗？", a: "有免费客户停车位。地铁MRT Wang Thonglang站步行约6分钟。" },
    ],
    ctaCall: "立即致电",
    ctaLine: "LINE咨询",
    breadcrumbHome: "首页",
    breadcrumbSelf: "联系我们",
  },

  ja: {
    metaTitle: "Thai Notary Law お問い合わせ — バンコクの公証・領事認証",
    metaDesc:
      "バンコクの公証弁護士へお問い合わせ。LINE・電話・メール・ワントンラン本店住所（バンコク10310）。月〜日 09:00-18:00 営業、見積無料。",
    eyebrow: "日本語 · お問い合わせ",
    heroTitle: "バンコクの公証弁護士へ今すぐご相談",
    heroSubtitle: "LINEで無料相談 · 当日見積 · 月〜日 09:00-18:00 ご来所歓迎。",
    answerLabel: "要約回答",
    answer:
      "Thai Notary Law本店はバンコク・ワントンラン区62/1（郵便番号10310）。LINE ID @Thainotary、電話 +66 94 895 8999、メール Contact@thainotary.co.th、営業時間は月〜日 09:00-18:00。書面確定見積は1営業時間以内に発行、バンコク及び近郊県へ当日出張公証可能です。",
    contactHeading: "ご連絡方法",
    intro:
      "最速の対応にはLINEに書類の写真をお送りください。1営業時間以内に書面で確定料金をご回答します。ワントンラン本店へは週7日ご来所可能。法人のお客様は発注書決済・税務適格請求書発行・当日出張公証（バンコク、ノンタブリー、パトゥムターニー、サムットプラーカーン、サムットサーコーン、ナコーンパトム）を承ります。",
    headOfficeLabel: "本店住所",
    hoursLabel: "営業時間",
    lineLabel: "LINE",
    phoneLabel: "電話",
    emailLabel: "メール",
    quickHelpHeading: "シーン別クイックガイド",
    quickHelp: [
      { title: "海外留学", body: "パスポート・卒業証書・成績証明書をご持参ください。公証＋タイ外務省＋大使館認証まで一貫対応。" },
      { title: "海外での結婚・離婚", body: "宣誓供述書・宣誓翻訳を作成・公証し、米・英・独・日・豪・EU等の大使館認証まで実施。" },
      { title: "会社・取締役決議", body: "法人書類の当日公証。委任状・株主決議は電子署名原本にも対応。" },
      { title: "海外不動産", body: "公証委任状・法定宣言・パスポート認証、世界各国の登記所で受理実績。" },
    ],
    bookingHeading: "ご予約のコツ",
    bookingBullets: [
      "LINEに書類写真を送ると最短で確定見積が届きます（通常1営業時間以内）。",
      "公証当日は原本と有効な身分証（外国籍はパスポート）をご持参ください。",
      "14:00までにご来所で当日発行。14:00以降は翌営業日予約。",
      "外務省＋大使館ルートは相手国により3〜7営業日目安。",
      "法人月次請求（Net-15）はお申し出ください。",
    ],
    faqHeading: "お問い合わせに関するFAQ",
    faq: [
      { q: "LINEの返信はどれくらいで来ますか？", a: "営業時間内（毎日09:00-18:00）1営業時間以内に返信します。時間外は翌朝一番でご返信します。" },
      { q: "初回相談は有料ですか？", a: "無料です。見積・書類確認・工程プランニングまで無料でご対応します。" },
      { q: "ホテル・病院への出張公証は可能ですか？", a: "はい、バンコク首都圏へ出張可能です。出張費は事前に書面でご案内します。" },
      { q: "支払方法は？", a: "Visa、Mastercard、JCB、PromptPay QRに対応。法人はNet-15振込も可能です。" },
      { q: "米ドル・ユーロ・円で支払えますか？", a: "はい、法律サービス請求書はUSD/EUR/GBP/JPY/CNY/KRW/VND/IDRの国際送金に対応。" },
      { q: "駐車場はありますか？", a: "無料のお客様駐車場あり。MRTワントンラン駅から徒歩約6分です。" },
    ],
    ctaCall: "今すぐ電話",
    ctaLine: "LINEで相談",
    breadcrumbHome: "ホーム",
    breadcrumbSelf: "お問い合わせ",
  },

  ko: {
    metaTitle: "Thai Notary Law 문의 — 방콕 공증 및 영사인증",
    metaDesc:
      "방콕 공증변호사에게 바로 문의하세요. LINE·전화·이메일 또는 왕통랑 사무소(방콕 10310) 방문 상담이 가능합니다. 월~일 09:00-18:00 운영하며 서류 확인 후 견적을 무료로 안내해 드립니다.",
    eyebrow: "한국어 · 문의",
    heroTitle: "방콕 공증변호사에게 지금 연락하세요",
    heroSubtitle: "LINE 무료 상담 · 당일 견적 · 월~일 09:00-18:00 방문 환영.",
    answerLabel: "요약 답변",
    answer:
      "Thai Notary Law 본사는 방콕 왕통랑구 62/1(우편번호 10310)에 위치. LINE ID @Thainotary, 전화 +66 94 895 8999, 이메일 Contact@thainotary.co.th, 영업시간 월~일 09:00-18:00. 서면 확정 견적은 1영업시간 내 발송, 방콕 및 인근 도로 당일 출장 공증 가능.",
    contactHeading: "연락 방법",
    intro:
      "가장 빠른 응답을 원하시면 LINE으로 필요한 문서 사진을 보내주세요. 1영업시간 내 서면 확정 견적을 회신드립니다. 왕통랑 본사는 주 7일 방문 가능하며, 기업 고객은 발주서 결제, 세금계산서 발행, 당일 출장 공증(방콕, 논타부리, 파툼타니, 사뭇쁘라깐, 사뭇사콘, 나콘빠톰)이 가능합니다.",
    headOfficeLabel: "본사 주소",
    hoursLabel: "영업 시간",
    lineLabel: "LINE",
    phoneLabel: "전화",
    emailLabel: "이메일",
    quickHelpHeading: "상황별 빠른 가이드",
    quickHelp: [
      { title: "해외 유학", body: "여권, 학위증, 성적증명서를 지참하세요. 공증+태국 외무부+대사관 인증까지 원스톱 처리합니다." },
      { title: "해외 결혼·이혼", body: "선서진술서, 선서번역을 작성·공증하고 미국·영국·독일·일본·호주·EU 등 대사관 인증까지 진행합니다." },
      { title: "회사·이사회 결의", body: "법인 문서 당일 공증. 위임장 및 주주 결의는 전자서명 원본도 접수합니다." },
      { title: "해외 부동산", body: "공증 위임장, 법정 선언, 여권 인증 - 전 세계 등기소에서 수리된 실적." },
    ],
    bookingHeading: "예약 팁",
    bookingBullets: [
      "LINE으로 문서 사진 전송이 가장 빠른 확정 견적을 받는 방법(보통 1영업시간 내).",
      "공증 당일 원본과 유효한 신분증(외국인은 여권)을 지참하세요.",
      "14:00 이전 방문 시 당일 처리 가능, 14:00 이후는 익영업일 예약.",
      "외무부+대사관 절차는 대상국에 따라 3~7영업일 소요.",
      "기업 월정산(Net-15) 계좌는 별도 요청 바랍니다.",
    ],
    faqHeading: "연락 관련 자주 묻는 질문",
    faq: [
      { q: "LINE 답변은 얼마나 걸리나요?", a: "영업시간 내(매일 09:00-18:00) 1영업시간 내 회신합니다. 영업 외 시간은 다음날 아침 첫 회신합니다." },
      { q: "초기 상담은 유료인가요?", a: "무료입니다. 견적, 문서 검토, 절차 계획까지 모두 무료로 제공합니다." },
      { q: "호텔이나 병원에 방문 공증이 가능한가요?", a: "네, 방콕 광역권 어디든 방문 공증이 가능합니다. 출장비는 사전 서면으로 안내드립니다." },
      { q: "결제 방법은?", a: "Visa, Mastercard, JCB, PromptPay QR 결제 가능. 법인은 Net-15 계좌이체도 가능합니다." },
      { q: "미화·유로·위안으로 결제 가능한가요?", a: "네, 법률 서비스 인보이스는 USD/EUR/GBP/JPY/CNY/KRW/VND/IDR 국제 송금이 가능합니다." },
      { q: "주차장이 있나요?", a: "무료 고객 주차장이 있습니다. MRT 왕통랑역에서 도보 약 6분 거리입니다." },
    ],
    ctaCall: "지금 전화",
    ctaLine: "LINE 상담",
    breadcrumbHome: "홈",
    breadcrumbSelf: "문의",
  },

  vi: {
    metaTitle: "Liên hệ Thai Notary Law — Công chứng & Hợp pháp hóa Bangkok",
    metaDesc:
      "Liên hệ luật sư công chứng có giấy phép tại Bangkok. LINE, điện thoại, email và địa chỉ Wang Thonglang (Bangkok 10310). Mở cửa Thứ Hai-Chủ Nhật 09:00-18:00.",
    eyebrow: "Tiếng Việt · Liên hệ",
    heroTitle: "Liên hệ luật sư công chứng Bangkok ngay hôm nay",
    heroSubtitle: "Tư vấn miễn phí qua LINE · báo giá trong ngày · chào đón khách vãng lai Thứ Hai-Chủ Nhật 09:00-18:00.",
    answerLabel: "Tóm tắt trả lời",
    answer:
      "Trụ sở Thai Notary Law tại 62/1 Wang Thonglang, Bangkok 10310 (LINE @Thainotary, điện thoại +66 94 895 8999, email Contact@thainotary.co.th), mở cửa Thứ Hai đến Chủ Nhật 09:00-18:00. Báo giá cố định bằng văn bản trong vòng một giờ làm việc. Công chứng lưu động có sẵn tại Bangkok và các tỉnh lân cận với dịch vụ điều động trong ngày.",
    contactHeading: "Cách liên hệ",
    intro:
      "Để phản hồi nhanh nhất, gửi ảnh tài liệu cần chứng thực qua LINE và chúng tôi sẽ trả lời bằng báo giá cố định trong vòng một giờ làm việc. Khách vãng lai được chào đón tại trụ sở Wang Thonglang mọi ngày trong tuần. Với khách hàng doanh nghiệp, chúng tôi chấp nhận đơn đặt hàng, xuất hóa đơn thuế và có thể cử luật sư đến văn phòng bạn tại Bangkok, Nonthaburi, Pathum Thani, Samut Prakan, Samut Sakhon hoặc Nakhon Pathom trong cùng ngày.",
    headOfficeLabel: "Trụ sở",
    hoursLabel: "Giờ mở cửa",
    lineLabel: "LINE",
    phoneLabel: "Điện thoại",
    emailLabel: "Email",
    quickHelpHeading: "Hướng dẫn nhanh theo tình huống",
    quickHelp: [
      { title: "Du học", body: "Mang hộ chiếu, bằng cấp và bảng điểm. Chúng tôi xử lý công chứng + Bộ Ngoại giao + hợp pháp hóa đại sứ quán từ đầu đến cuối." },
      { title: "Kết hôn hoặc ly hôn ở nước ngoài", body: "Chúng tôi soạn thảo và công chứng tuyên thệ, dịch tuyên thệ và hợp pháp hóa cho các đại sứ quán (Mỹ, Anh, Đức, Nhật, Úc, EU)." },
      { title: "Nghị quyết công ty/hội đồng", body: "Công chứng tài liệu doanh nghiệp trong ngày; chúng tôi chấp nhận bản gốc ký điện tử cho ủy quyền và nghị quyết cổ đông." },
      { title: "Bất động sản nước ngoài", body: "Ủy quyền công chứng, tuyên bố theo luật và chứng thực hộ chiếu được các văn phòng đất đai trên toàn thế giới chấp nhận." },
    ],
    bookingHeading: "Mẹo đặt lịch",
    bookingBullets: [
      "Gửi ảnh tài liệu trên LINE để nhận báo giá cố định nhanh nhất (thường trong vòng một giờ làm việc).",
      "Mang tài liệu gốc và CMND/hộ chiếu còn hạn vào ngày công chứng.",
      "Đến trước 14:00 để có dịch vụ trong ngày; sau 14:00 chúng tôi đặt lịch cho ngày làm việc kế tiếp.",
      "Với chuỗi Bộ Ngoại giao + đại sứ quán, cần 3-7 ngày làm việc tùy quốc gia tiếp nhận.",
      "Tài khoản doanh nghiệp (thanh toán Net-15) có sẵn theo yêu cầu.",
    ],
    faqHeading: "Câu hỏi thường gặp về liên hệ",
    faq: [
      { q: "Bạn trả lời LINE nhanh thế nào?", a: "Trong vòng một giờ làm việc trong giờ mở cửa (09:00-18:00 hàng ngày). Ngoài giờ chúng tôi trả lời đầu giờ sáng hôm sau." },
      { q: "Có tính phí tư vấn ban đầu không?", a: "Không. Tất cả báo giá và tư vấn ban đầu đều miễn phí - bao gồm xem xét tài liệu và lập kế hoạch quy trình." },
      { q: "Bạn có thể cử luật sư đến khách sạn hoặc bệnh viện của tôi không?", a: "Có, công chứng lưu động có sẵn khắp khu vực đô thị Bangkok. Phí đi lại được báo giá trước khi khởi hành." },
      { q: "Bạn chấp nhận thẻ tín dụng không?", a: "Có - Visa, Mastercard, JCB và PromptPay QR. Chuyển khoản doanh nghiệp cũng được chấp nhận với điều khoản Net-15 theo yêu cầu." },
      { q: "Tôi có thể thanh toán bằng USD, EUR hoặc CNY không?", a: "Có, chuyển khoản quốc tế bằng USD, EUR, GBP, JPY, CNY, KRW, VND, IDR được chấp nhận cho hóa đơn dịch vụ pháp lý." },
      { q: "Có bãi đỗ xe không?", a: "Có, bãi đỗ xe miễn phí cho khách hàng tại văn phòng Wang Thonglang. MRT Wang Thonglang cách 6 phút đi bộ." },
    ],
    ctaCall: "Gọi ngay",
    ctaLine: "Chat LINE",
    breadcrumbHome: "Trang chủ",
    breadcrumbSelf: "Liên hệ",
  },

  id: {
    metaTitle: "Kontak Thai Notary Law — Notaris & Legalisasi Bangkok",
    metaDesc:
      "Hubungi notaris berlisensi di Bangkok hari ini. LINE, telepon, email dan alamat Wang Thonglang (Bangkok 10310). Buka Senin-Minggu 09:00-18:00. Penawaran gratis.",
    eyebrow: "Bahasa Indonesia · Kontak",
    heroTitle: "Hubungi notaris berlisensi Bangkok hari ini",
    heroSubtitle: "Konsultasi gratis via LINE · penawaran hari yang sama · dipersilakan tanpa janji Senin-Minggu 09:00-18:00.",
    answerLabel: "Ringkasan jawaban",
    answer:
      "Kantor pusat Thai Notary Law berada di 62/1 Wang Thonglang, Bangkok 10310 (LINE @Thainotary, telepon +66 94 895 8999, email Contact@thainotary.co.th), buka Senin sampai Minggu 09:00-18:00. Penawaran tertulis gratis dikeluarkan dalam satu jam kerja. Notarisasi keliling tersedia di seluruh Bangkok dan provinsi tetangga dengan pengiriman hari yang sama.",
    contactHeading: "Cara menghubungi",
    intro:
      "Untuk respons tercepat, kirim foto dokumen yang perlu disertifikasi via LINE dan kami akan membalas dengan penawaran tertulis dalam satu jam kerja. Kami menyambut kedatangan langsung di kantor pusat Wang Thonglang setiap hari. Untuk klien korporat, kami menerima purchase order, menerbitkan faktur pajak, dan dapat mengirim notaris ke kantor Anda di Bangkok, Nonthaburi, Pathum Thani, Samut Prakan, Samut Sakhon atau Nakhon Pathom di hari yang sama.",
    headOfficeLabel: "Kantor pusat",
    hoursLabel: "Jam buka",
    lineLabel: "LINE",
    phoneLabel: "Telepon",
    emailLabel: "Email",
    quickHelpHeading: "Bantuan cepat per skenario",
    quickHelp: [
      { title: "Studi di luar negeri", body: "Bawa paspor, ijazah dan transkrip. Kami menangani notaris + Kemenlu Thai + legalisasi kedutaan dari awal hingga akhir." },
      { title: "Pernikahan atau perceraian di luar negeri", body: "Kami menyiapkan dan menotarisasi affidavit, terjemahan tersumpah, dan melegalisasinya untuk kedutaan (AS, Inggris, Jerman, Jepang, Australia, UE)." },
      { title: "Resolusi perusahaan/dewan", body: "Notarisasi dokumen korporat hari yang sama; kami menerima asli yang ditandatangani secara elektronik untuk kuasa dan resolusi pemegang saham." },
      { title: "Properti luar negeri", body: "Surat kuasa yang dinotarisasi, deklarasi resmi dan sertifikasi paspor diterima kantor pertanahan di seluruh dunia." },
    ],
    bookingHeading: "Tips pemesanan",
    bookingBullets: [
      "Kirim foto dokumen di LINE untuk penawaran tetap tercepat (biasanya dalam satu jam kerja).",
      "Bawa dokumen asli dan ID yang berlaku (paspor untuk WNA) pada hari notarisasi.",
      "Tiba sebelum 14:00 untuk layanan hari yang sama; setelah 14:00 kami pesan untuk hari kerja berikutnya.",
      "Untuk rantai Kemenlu + kedutaan, siapkan 3-7 hari kerja tergantung negara penerima.",
      "Akun korporat (invoicing Net-15) tersedia atas permintaan.",
    ],
    faqHeading: "FAQ kontak",
    faq: [
      { q: "Seberapa cepat Anda membalas di LINE?", a: "Dalam satu jam kerja selama jam buka (09:00-18:00 setiap hari). Di luar jam kami membalas hal pertama pagi berikutnya." },
      { q: "Apakah konsultasi awal dikenakan biaya?", a: "Tidak. Semua penawaran dan konsultasi awal gratis - termasuk review dokumen dan perencanaan alur kerja." },
      { q: "Bisakah Anda mengirim notaris ke hotel atau rumah sakit saya?", a: "Ya, notarisasi keliling tersedia di seluruh area metropolitan Bangkok. Biaya perjalanan dikutip sebelum pengiriman." },
      { q: "Apakah Anda menerima kartu kredit?", a: "Ya - Visa, Mastercard, JCB, dan PromptPay QR. Transfer kawat korporat juga diterima dengan syarat Net-15 atas permintaan." },
      { q: "Bisakah saya membayar dalam USD, EUR atau CNY?", a: "Ya, transfer bank internasional dalam USD, EUR, GBP, JPY, CNY, KRW, VND, IDR diterima untuk faktur layanan hukum." },
      { q: "Apakah ada tempat parkir?", a: "Ya, parkir pelanggan gratis tersedia di kantor Wang Thonglang. MRT Wang Thonglang berjarak jalan kaki 6 menit." },
    ],
    ctaCall: "Telepon sekarang",
    ctaLine: "Chat di LINE",
    breadcrumbHome: "Beranda",
    breadcrumbSelf: "Kontak",
  },
};

/* ------------------------------------------------------------------ */
/* COVERAGE */
/* ------------------------------------------------------------------ */

export const LOCALE_COVERAGE: Record<LocaleCode, LocaleCoverageContent> = {
  en: {
    metaTitle: "Coverage — 77 Provinces + 50 Bangkok Districts | Thai Notary Law",
    metaDesc:
      "Nationwide legalization services in Thailand. Bangkok notary service plus all 77 provinces and 50 Bangkok districts. 4 offices + partner attorney network.",
    eyebrow: "English · Coverage",
    heroTitle: "77 provinces + 50 Bangkok districts",
    heroSubtitle: "4 offices (Bangkok · Khon Kaen · Udon Thani · Nong Khai) + nationwide partner attorneys.",
    answerLabel: "Answer summary",
    answer:
      "Thai Notary Law provides nationwide notary and legalization coverage across all 77 provinces of Thailand and all 50 districts of Bangkok. Four staffed offices in Bangkok, Khon Kaen, Udon Thani and Nong Khai are supported by a partner attorney network so we can dispatch a licensed notary to any provincial capital within one business day, and typically same-day within the Bangkok Metropolitan Area.",
    overviewHeading: "Nationwide coverage",
    intro:
      "Whether you need Bangkok notary service in a Sukhumvit hotel, a certified true copy in a Khon Kaen university office, or MOFA legalization for a document originating in Nong Khai on the Laos border, our four staffed offices plus partner attorney network cover every province in Thailand. Detailed per-province and per-district hubs are indexed below with unique local content — economic sectors, embassy access, MOFA logistics — so that both search engines and AI answer engines can route users to the exact area they need.",
    officesHeading: "Our offices",
    officeBullets: [
      "Bangkok (HQ): 62/1 Wang Thonglang, Bangkok 10310 — 6 attorneys on rota, walk-ins Mon-Sun 09:00-18:00.",
      "Khon Kaen: Northeast regional office serving Isan universities, hospitals and border trade.",
      "Udon Thani: Northeast + Laos border corridor, MOFA/Vientiane visa runners.",
      "Nong Khai: Thai-Laos Friendship Bridge, cross-border property and marriage documents.",
    ],
    bangkokHeading: "Bangkok — 50 districts",
    provincesHeading: "77 provinces",
    regionLabels: {
      bangkok: "Bangkok",
      central: "Central",
      north: "North",
      northeast: "Northeast",
      east: "East",
      west: "West",
      south: "South",
    },
    bangkokDistrictsLabel: "Bangkok districts",
    perAreaCaption: "Every province and district has its own hub page with unique local content and workflow examples.",
    faqHeading: "Coverage FAQ",
    faq: [
      { q: "Can you notarize outside Bangkok?", a: "Yes. We staff four offices (Bangkok, Khon Kaen, Udon Thani, Nong Khai) and dispatch partner attorneys to any provincial capital within one business day." },
      { q: "Do you cover the deep south (Yala, Pattani, Narathiwat)?", a: "Yes, via our southern partner attorney network. Turnaround is typically 2-3 business days for on-site notarization." },
      { q: "How does the MOFA + embassy leg work if I'm outside Bangkok?", a: "We collect the notarized document via secure courier or in-person handoff, run it through MOFA in Bangkok, then present at the receiving embassy on your behalf — usually 3-7 business days end-to-end." },
      { q: "Is mobile notarization more expensive?", a: "The notarial fee itself is unchanged. A transparent travel fee is added and quoted in writing before dispatch." },
      { q: "Can you meet at a hospital ICU or hospice for urgent notarization?", a: "Yes. Urgent hospital/hospice notarization is dispatched within 3 hours in Bangkok and same-day for nearby provinces." },
    ],
    breadcrumbHome: "Home",
    breadcrumbSelf: "Coverage",
  },

  zh: {
    metaTitle: "服务覆盖 — 泰国77府 + 曼谷50区 | Thai Notary Law",
    metaDesc:
      "全泰公证与领事认证服务，覆盖曼谷全部50区及全国77府。我们拥有自营办公室与全国合作律师网络，可安排上门取件、代办外交部认证与使馆认证，中文沟通全程跟进。",
    eyebrow: "中文 · 服务覆盖",
    heroTitle: "覆盖泰国77府 + 曼谷50区",
    heroSubtitle: "4间办公室（曼谷·孔敬·乌隆他尼·廊开）+ 全国合作律师。",
    answerLabel: "快速回答",
    answer:
      "Thai Notary Law覆盖泰国全部77府及曼谷50区。曼谷、孔敬、乌隆他尼、廊开四间自营办公室，配合全国合作律师网络，可在一个工作日内派遣持证公证律师至任一府会城市，曼谷都会区通常当日达。",
    overviewHeading: "全国覆盖",
    intro:
      "无论您是在素坤逸酒店办理曼谷公证、在孔敬大学办公室办理认证副本，还是在老挝边境廊开办理需泰国外交部认证的文件，本所四间办公室加全国合作律师网络均可覆盖。以下按府/按区索引每个地区专属页面，内容独一无二 — 涵盖当地经济特色、使馆通道、外交部物流 — 便于搜索引擎与AI问答引擎精准引导用户至所需地区。",
    officesHeading: "本所办公室",
    officeBullets: [
      "曼谷（总部）：62/1 Wang Thonglang, Bangkok 10310 — 6位律师轮值，周一至周日09:00-18:00欢迎到访。",
      "孔敬：东北区域办公室，服务伊桑地区大学、医院及边境贸易。",
      "乌隆他尼：东北+老挝边境走廊，外交部/万象签证代办。",
      "廊开：泰老友谊桥，跨境房产及婚姻文件专办。",
    ],
    bangkokHeading: "曼谷 — 50区",
    provincesHeading: "77府",
    regionLabels: {
      bangkok: "曼谷",
      central: "中部",
      north: "北部",
      northeast: "东北部",
      east: "东部",
      west: "西部",
      south: "南部",
    },
    bangkokDistrictsLabel: "曼谷各区",
    perAreaCaption: "每一府、每一区均有独立专属页面，涵盖本地内容与实际流程示例。",
    faqHeading: "覆盖相关常见问题",
    faq: [
      { q: "曼谷以外可以办公证吗？", a: "可以。本所四间自营办公室及合作律师网络可在一个工作日内派员至任一府会城市。" },
      { q: "南部（也拉、北大年、那拉提瓦）也覆盖吗？", a: "覆盖，通过南部合作律师办理，现场公证通常需2-3个工作日。" },
      { q: "外地文件的外交部+使馆流程如何？", a: "本所派专人或安全快递收件，在曼谷办理外交部认证后再送至使馆认证，通常3-7个工作日全流程完成。" },
      { q: "上门公证会更贵吗？", a: "公证费本身不变，仅按透明标准加收差旅费，派遣前书面报价。" },
      { q: "医院ICU或临终关怀能紧急公证吗？", a: "可以。曼谷区3小时内派员，周边府当日达。" },
    ],
    breadcrumbHome: "首页",
    breadcrumbSelf: "服务覆盖",
  },

  ja: {
    metaTitle: "対応エリア — タイ全77県 + バンコク50区 | Thai Notary Law",
    metaDesc:
      "タイ全国の公証・領事認証サービス。バンコク中心＋全77県・50区、自営4支所＋全国提携弁護士網。",
    eyebrow: "日本語 · 対応エリア",
    heroTitle: "タイ全77県 + バンコク50区に対応",
    heroSubtitle: "4事務所（バンコク・コーンケン・ウドンタニ・ノーンカーイ）＋全国提携弁護士。",
    answerLabel: "要約回答",
    answer:
      "Thai Notary Lawはタイ全77県及びバンコク50区を全面カバー。バンコク・コーンケン・ウドンタニ・ノーンカーイの4自営事務所と全国提携弁護士網により、任意の県庁所在地へ1営業日以内、バンコク首都圏は通常当日中に公証弁護士を派遣可能です。",
    overviewHeading: "全国対応",
    intro:
      "スクンビットのホテルでのバンコク公証、コーンケン大学事務局での認証謄本、ラオス国境ノーンカーイ発文書のタイ外務省認証 — いずれも本所4事務所＋全国提携弁護士網でカバー可能。以下、県別・区別の専用ハブページを索引しており、地元経済・大使館アクセス・外務省物流など固有情報を掲載。検索エンジン及びAI応答エンジンから利用者を該当エリアへ的確に誘導します。",
    officesHeading: "本所拠点",
    officeBullets: [
      "バンコク（本店）：62/1 Wang Thonglang, Bangkok 10310 — 弁護士6名交代勤務、月〜日09:00-18:00ご来所歓迎。",
      "コーンケン：東北地域事務所、イサーン地方大学・病院・国境貿易対応。",
      "ウドンタニ：東北＋ラオス国境回廊、外務省/ビエンチャンビザ運搬。",
      "ノーンカーイ：タイ・ラオス友好橋、越境不動産・婚姻書類。",
    ],
    bangkokHeading: "バンコク — 50区",
    provincesHeading: "全77県",
    regionLabels: {
      bangkok: "バンコク",
      central: "中部",
      north: "北部",
      northeast: "東北部",
      east: "東部",
      west: "西部",
      south: "南部",
    },
    bangkokDistrictsLabel: "バンコク各区",
    perAreaCaption: "全ての県及び区に固有の地元情報と業務事例を掲載した専用ハブページがあります。",
    faqHeading: "対応エリアFAQ",
    faq: [
      { q: "バンコク外でも公証可能ですか？", a: "可能です。自営4事務所＋提携弁護士網により1営業日以内に県庁所在地へ派遣可能。" },
      { q: "南部深部（ヤラー、パッタニー、ナラーティワート）も対応しますか？", a: "南部提携弁護士経由で対応、現場公証は通常2-3営業日。" },
      { q: "地方文書の外務省＋大使館ルートは？", a: "本所が安全宅配又は直接引取で回収し、バンコクにて外務省・大使館認証まで一貫代行、通常3-7営業日。" },
      { q: "出張公証は割高になりますか？", a: "公証料自体は不変、透明な出張費のみ加算、派遣前に書面提示。" },
      { q: "病院ICU・ホスピスでの緊急公証は？", a: "可能です。バンコク区内3時間、近郊県は当日中に派遣。" },
    ],
    breadcrumbHome: "ホーム",
    breadcrumbSelf: "対応エリア",
  },

  ko: {
    metaTitle: "서비스 지역 — 태국 77개 도 + 방콕 50개 구 | Thai Notary Law",
    metaDesc:
      "태국 전국 공증 및 영사인증 서비스. 방콕 중심+전 77개 도 50개 구, 자체 4개 사무소+전국 제휴 변호사 네트워크.",
    eyebrow: "한국어 · 서비스 지역",
    heroTitle: "태국 77개 도 + 방콕 50개 구 커버",
    heroSubtitle: "4개 사무소(방콕·콘깬·우돈타니·농카이)+전국 제휴 변호사.",
    answerLabel: "요약 답변",
    answer:
      "Thai Notary Law는 태국 전 77개 도 및 방콕 50개 구를 완전 커버합니다. 방콕·콘깬·우돈타니·농카이 4개 자체 사무소와 전국 제휴 변호사 네트워크를 통해 어느 도청 소재지로도 1영업일 이내 파견 가능하며, 방콕 광역권은 통상 당일 파견 가능합니다.",
    overviewHeading: "전국 커버리지",
    intro:
      "수쿰빗 호텔에서의 방콕 공증, 콘깬 대학 사무실에서의 인증사본, 라오스 국경 농카이에서 발생한 문서의 태국 외무부 인증 — 어떤 경우든 4개 사무소와 전국 제휴 변호사 네트워크로 커버됩니다. 아래 도별·구별 전용 허브 페이지에는 지역 경제·대사관 접근·외무부 물류 등 고유 정보가 색인되어 있어 검색엔진과 AI 응답엔진이 필요한 지역으로 사용자를 정확히 안내할 수 있습니다.",
    officesHeading: "사무소 안내",
    officeBullets: [
      "방콕(본사): 62/1 Wang Thonglang, Bangkok 10310 — 변호사 6명 순번 근무, 월~일 09:00-18:00 방문 환영.",
      "콘깬: 동북부 지역 사무소, 이싼 지역 대학·병원·국경 무역 담당.",
      "우돈타니: 동북부+라오스 국경 회랑, 외무부/비엔티안 비자 대행.",
      "농카이: 태-라오스 우정의 다리, 국경 부동산 및 혼인 서류 전담.",
    ],
    bangkokHeading: "방콕 — 50개 구",
    provincesHeading: "77개 도",
    regionLabels: {
      bangkok: "방콕",
      central: "중부",
      north: "북부",
      northeast: "동북부",
      east: "동부",
      west: "서부",
      south: "남부",
    },
    bangkokDistrictsLabel: "방콕 구 목록",
    perAreaCaption: "모든 도 및 구에는 고유 지역 콘텐츠와 실제 업무 사례가 담긴 전용 허브 페이지가 있습니다.",
    faqHeading: "서비스 지역 FAQ",
    faq: [
      { q: "방콕 외 지역에서 공증이 가능한가요?", a: "네. 4개 자체 사무소와 제휴 변호사 네트워크로 어느 도청 소재지든 1영업일 이내 파견 가능합니다." },
      { q: "남부(얄라·파타니·나라티왓)도 커버되나요?", a: "네, 남부 제휴 변호사를 통해 처리하며 현장 공증은 통상 2-3영업일 소요됩니다." },
      { q: "지방 문서의 외무부+대사관 처리는 어떻게 되나요?", a: "본 사무소가 안전 택배 또는 직접 수령으로 문서를 회수한 후 방콕에서 외무부·대사관 인증까지 대행합니다. 통상 3-7영업일 소요." },
      { q: "방문 공증이 더 비싼가요?", a: "공증 수수료 자체는 동일하며, 투명한 출장비만 별도 청구하고 파견 전 서면으로 안내합니다." },
      { q: "병원 중환자실이나 호스피스에서 긴급 공증이 가능한가요?", a: "가능합니다. 방콕 시내 3시간 이내, 인근 도는 당일 파견입니다." },
    ],
    breadcrumbHome: "홈",
    breadcrumbSelf: "서비스 지역",
  },

  vi: {
    metaTitle: "Vùng phục vụ — 77 tỉnh + 50 quận Bangkok | Thai Notary Law",
    metaDesc:
      "Dịch vụ công chứng và hợp pháp hóa toàn Thái Lan. Bangkok cùng toàn bộ 77 tỉnh và 50 quận, 4 văn phòng+mạng lưới luật sư đối tác toàn quốc.",
    eyebrow: "Tiếng Việt · Vùng phục vụ",
    heroTitle: "77 tỉnh + 50 quận Bangkok",
    heroSubtitle: "4 văn phòng (Bangkok·Khon Kaen·Udon Thani·Nong Khai)+mạng lưới luật sư đối tác toàn quốc.",
    answerLabel: "Tóm tắt trả lời",
    answer:
      "Thai Notary Law phục vụ toàn bộ 77 tỉnh Thái Lan và 50 quận Bangkok. Bốn văn phòng có nhân sự tại Bangkok, Khon Kaen, Udon Thani và Nong Khai cùng mạng lưới luật sư đối tác cho phép chúng tôi cử luật sư công chứng có giấy phép đến bất kỳ thủ phủ tỉnh nào trong một ngày làm việc, thường trong ngày với khu vực Đô thị Bangkok.",
    overviewHeading: "Vùng phục vụ toàn quốc",
    intro:
      "Cho dù bạn cần công chứng Bangkok tại khách sạn Sukhumvit, bản sao chứng thực tại văn phòng đại học Khon Kaen, hay hợp pháp hóa Bộ Ngoại giao cho tài liệu xuất phát từ Nong Khai ở biên giới Lào, bốn văn phòng có nhân sự cùng mạng lưới luật sư đối tác của chúng tôi phủ mọi tỉnh ở Thái Lan. Các trung tâm theo tỉnh và theo quận được lập chỉ mục dưới đây với nội dung địa phương độc đáo - ngành kinh tế, tiếp cận đại sứ quán, hậu cần Bộ Ngoại giao - để cả công cụ tìm kiếm và công cụ trả lời AI có thể định tuyến người dùng đến khu vực chính xác họ cần.",
    officesHeading: "Văn phòng của chúng tôi",
    officeBullets: [
      "Bangkok (Trụ sở): 62/1 Wang Thonglang, Bangkok 10310 - 6 luật sư luân phiên, khách vãng lai Thứ Hai-Chủ Nhật 09:00-18:00.",
      "Khon Kaen: Văn phòng vùng Đông Bắc phục vụ đại học, bệnh viện và thương mại biên giới Isan.",
      "Udon Thani: Hành lang biên giới Đông Bắc+Lào, chuyển thị thực Bộ Ngoại giao/Vientiane.",
      "Nong Khai: Cầu Hữu nghị Thái-Lào, tài liệu bất động sản và hôn nhân xuyên biên giới.",
    ],
    bangkokHeading: "Bangkok — 50 quận",
    provincesHeading: "77 tỉnh",
    regionLabels: {
      bangkok: "Bangkok",
      central: "Miền Trung",
      north: "Miền Bắc",
      northeast: "Đông Bắc",
      east: "Miền Đông",
      west: "Miền Tây",
      south: "Miền Nam",
    },
    bangkokDistrictsLabel: "Các quận Bangkok",
    perAreaCaption: "Mỗi tỉnh và quận đều có trang trung tâm riêng với nội dung địa phương độc đáo và ví dụ quy trình.",
    faqHeading: "FAQ vùng phục vụ",
    faq: [
      { q: "Bạn có thể công chứng ngoài Bangkok không?", a: "Có. Chúng tôi có 4 văn phòng nhân sự (Bangkok, Khon Kaen, Udon Thani, Nong Khai) và cử luật sư đối tác đến bất kỳ thủ phủ tỉnh nào trong một ngày làm việc." },
      { q: "Bạn có phủ vùng cực nam (Yala, Pattani, Narathiwat) không?", a: "Có, thông qua mạng lưới luật sư đối tác miền nam. Thời gian xử lý thường 2-3 ngày làm việc cho công chứng tại chỗ." },
      { q: "Quy trình Bộ Ngoại giao+đại sứ quán hoạt động thế nào nếu tôi ở ngoài Bangkok?", a: "Chúng tôi thu thập tài liệu đã công chứng qua chuyển phát bảo mật hoặc giao tận tay, chạy qua Bộ Ngoại giao ở Bangkok, sau đó trình tại đại sứ quán tiếp nhận thay bạn - thường 3-7 ngày làm việc từ đầu đến cuối." },
      { q: "Công chứng lưu động có đắt hơn không?", a: "Phí công chứng không đổi. Một phí đi lại minh bạch được thêm vào và báo giá bằng văn bản trước khi khởi hành." },
      { q: "Bạn có thể gặp tại ICU bệnh viện hoặc nhà tế bần cho công chứng khẩn cấp không?", a: "Có. Công chứng bệnh viện/nhà tế bần khẩn cấp được cử trong 3 giờ tại Bangkok và trong ngày cho các tỉnh lân cận." },
    ],
    breadcrumbHome: "Trang chủ",
    breadcrumbSelf: "Vùng phục vụ",
  },

  id: {
    metaTitle: "Wilayah layanan — 77 provinsi + 50 distrik Bangkok | Thai Notary Law",
    metaDesc:
      "Layanan notaris dan legalisasi seluruh Thailand. Bangkok plus seluruh 77 provinsi dan 50 distrik, 4 kantor+jaringan pengacara mitra nasional.",
    eyebrow: "Bahasa Indonesia · Wilayah",
    heroTitle: "77 provinsi + 50 distrik Bangkok",
    heroSubtitle: "4 kantor (Bangkok·Khon Kaen·Udon Thani·Nong Khai)+jaringan pengacara mitra nasional.",
    answerLabel: "Ringkasan jawaban",
    answer:
      "Thai Notary Law menyediakan cakupan notaris dan legalisasi nasional di seluruh 77 provinsi Thailand dan seluruh 50 distrik Bangkok. Empat kantor berstaf di Bangkok, Khon Kaen, Udon Thani dan Nong Khai didukung jaringan pengacara mitra sehingga kami dapat mengirim notaris berlisensi ke ibukota provinsi manapun dalam satu hari kerja, dan biasanya hari yang sama di dalam Wilayah Metropolitan Bangkok.",
    overviewHeading: "Cakupan nasional",
    intro:
      "Baik Anda butuh layanan notaris Bangkok di hotel Sukhumvit, salinan tersertifikasi di kantor universitas Khon Kaen, atau legalisasi Kementerian Luar Negeri Thailand untuk dokumen dari Nong Khai di perbatasan Laos, empat kantor berstaf plus jaringan pengacara mitra kami mencakup setiap provinsi di Thailand. Hub per-provinsi dan per-distrik terperinci diindeks di bawah dengan konten lokal unik - sektor ekonomi, akses kedutaan, logistik Kemenlu - agar mesin pencari maupun mesin jawaban AI dapat mengarahkan pengguna ke area tepat yang mereka butuhkan.",
    officesHeading: "Kantor kami",
    officeBullets: [
      "Bangkok (Pusat): 62/1 Wang Thonglang, Bangkok 10310 - 6 pengacara bergilir, tanpa janji Senin-Minggu 09:00-18:00.",
      "Khon Kaen: Kantor regional Timur Laut melayani universitas Isan, rumah sakit dan perdagangan perbatasan.",
      "Udon Thani: Koridor perbatasan Timur Laut+Laos, pengurus visa Kemenlu/Vientiane.",
      "Nong Khai: Jembatan Persahabatan Thai-Laos, dokumen properti dan pernikahan lintas batas.",
    ],
    bangkokHeading: "Bangkok — 50 distrik",
    provincesHeading: "77 provinsi",
    regionLabels: {
      bangkok: "Bangkok",
      central: "Tengah",
      north: "Utara",
      northeast: "Timur Laut",
      east: "Timur",
      west: "Barat",
      south: "Selatan",
    },
    bangkokDistrictsLabel: "Distrik Bangkok",
    perAreaCaption: "Setiap provinsi dan distrik memiliki halaman hub sendiri dengan konten lokal unik dan contoh alur kerja.",
    faqHeading: "FAQ cakupan wilayah",
    faq: [
      { q: "Bisakah Anda menotarisasi di luar Bangkok?", a: "Ya. Kami memiliki empat kantor berstaf (Bangkok, Khon Kaen, Udon Thani, Nong Khai) dan mengirim pengacara mitra ke ibukota provinsi manapun dalam satu hari kerja." },
      { q: "Apakah Anda mencakup ujung selatan (Yala, Pattani, Narathiwat)?", a: "Ya, melalui jaringan pengacara mitra selatan kami. Waktu pengerjaan biasanya 2-3 hari kerja untuk notarisasi di tempat." },
      { q: "Bagaimana leg Kemenlu+kedutaan bekerja jika saya di luar Bangkok?", a: "Kami mengumpulkan dokumen yang dinotarisasi via kurir aman atau serah terima langsung, memprosesnya melalui Kemenlu di Bangkok, kemudian mempresentasikan di kedutaan penerima atas nama Anda - biasanya 3-7 hari kerja dari awal hingga akhir." },
      { q: "Apakah notarisasi keliling lebih mahal?", a: "Biaya notaris itu sendiri tidak berubah. Biaya perjalanan transparan ditambahkan dan dikutip tertulis sebelum pengiriman." },
      { q: "Bisakah Anda bertemu di ICU rumah sakit atau hospice untuk notarisasi mendesak?", a: "Ya. Notarisasi rumah sakit/hospice mendesak dikirim dalam 3 jam di Bangkok dan hari yang sama untuk provinsi terdekat." },
    ],
    breadcrumbHome: "Beranda",
    breadcrumbSelf: "Wilayah",
  },
};
